import React, { useState, useEffect } from "react";
import {
  Table,
  Space,
  Modal,
  Form,
  Input,
  Button,
  Spin,
  Popconfirm,
  message,
  Tag,
  Descriptions,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { fetchBooks, deleteBook, updateBook } from "./BookService";
import {
  collection,
  addDoc,
  LoadBundleTask,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../Firebase/FirebaseConnection";
import "./Table.css";
import TextArea from "antd/es/input/TextArea";
import SearchBar from "./Search";
import Loading from "./Loading";

const AntTable = ({ setRefreshBooks }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [editingBook, setEditingBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [form] = Form.useForm();
  const [filteredBookList, setFilteredBookList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const availableCopies = parseInt(editingBook?.copiesAvailable) || 0;
  const conditionDisabled = availableCopies === 0;
  const [isRepairModalVisible, setIsRepairModalVisible] = useState(false);
  const [repairingBook, setRepairingBook] = useState(null);
  const [isRecoverModalVisible, setIsRecoverModalVisible] = useState(false);
  const [recoveringBook, setRecoveringBook] = useState(null);

  useEffect(() => {
    const booksRef = collection(db, "books");

    const unsubscribe = onSnapshot(booksRef, (snapshot) => {
      const booksData = snapshot.docs.map((doc) => ({
        key: doc.id,
        ...doc.data(),
      }));

      setBooks(booksData);
      setFilteredBookList(
        searchTerm
          ? booksData.filter(
              (book) =>
                book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                book.author.toLowerCase().includes(searchTerm.toLowerCase())
            )
          : booksData
      );

      if (selectedBook) {
        const updated = booksData.find((b) => b.key === selectedBook.key);
        if (updated) setSelectedBook(updated);
      }

      if (editingBook) {
        const updated = booksData.find((b) => b.key === editingBook.key);
        if (updated) {
          setEditingBook(updated);
          form.setFieldsValue(updated);
        }
      }
    });

    setRefreshBooks(() => () => {});

    return () => unsubscribe();
  }, [searchTerm]);

  const handleOpenRepairModal = (book) => {
    setRepairingBook(book);
    setIsRepairModalVisible(true);
  };

  const handleOpenRecoverModal = (book) => {
    setRecoveringBook(book);
    setIsRecoverModalVisible(true);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    const filtered = books.filter(
      (book) =>
        book.author.toLowerCase().includes(value.toLowerCase()) ||
        book.title.toLowerCase().includes(value.toLowerCase()) ||
        book.year.toLowerCase().includes(value.toLowerCase()) ||
        book.isbn.toLowerCase().includes(value.toLowerCase()) ||
        (typeof book.category === "string"
          ? book.category.toLowerCase().includes(value.toLowerCase())
          : Array.isArray(book.category)
          ? book.category.some(
              (cat) =>
                typeof cat === "string" &&
                cat.toLowerCase().includes(value.toLowerCase())
            )
          : false)
    );
    setFilteredBookList(filtered);
  };

  const logActivity = async (
    bookTitle,
    oldStatus,
    newStatus,
    actionType,
    fineAmount
  ) => {
    try {
      const userName = localStorage.getItem("userName");
      const logRef = collection(db, "user-activity-logs");

      const now = new Date();
      const formattedDateTime = now.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        actionType: actionType,
      });

      await addDoc(logRef, {
        bookTitle: bookTitle,
        oldStatus: oldStatus,
        newStatus: newStatus,
        fineAmount: fineAmount,
        updatedBy: userName,
        timestamp: now,
        formattedDateTime,
        actionType: actionType,
      });

      console.log(
        `Activity logged: ${bookTitle} status changed from ${oldStatus} to ${newStatus} with a fine of ₱${fineAmount}.`
      );
    } catch (error) {
      console.error("Failed to log activity:", error);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      fixed: "left",
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Copies Available",
      dataIndex: "copiesAvailable",
      key: "copiesAvailable",
    },
    {
      title: "Shelf Location",
      dataIndex: "shelfLocation",
      key: "shelfLocation",
      render: (shelfNumber) => (
        <Tag color="#996c6c" className="transparent-tag" key={shelfNumber}>
          {shelfNumber}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title="View Book Details"
            overlayInnerStyle={{ backgroundColor: "#FFFFFF", color: "#000000" }}
            color="#FFFFFF"
          >
            <Button
              type="link"
              icon={<EyeOutlined className="action-icon" />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip
            title="Edit Book Details "
            overlayInnerStyle={{ backgroundColor: "#FFFFFF", color: "#000000" }}
            color="#FFFFFF"
          >
            <Button
              type="link"
              icon={<EditOutlined className="action-icon" />}
              onClick={() => handleEditBook(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this book?"
            onConfirm={() => handleDeleteBook(record.key)}
            okText="Yes"
            cancelText="No"
            placement="left"
          >
            <Tooltip
              title="Delete Book"
              overlayInnerStyle={{
                backgroundColor: "#FFFFFF",
                color: "#000000",
              }}
              color="#FFFFFF"
            >
              <Button
                type="link"
                icon={<DeleteOutlined className="action-icon" />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleViewDetails = (record) => {
    setSelectedBook(record);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedBook(null);
  };

  const handleEditBook = (book) => {
    setEditingBook(book);
    setIsEditModalVisible(true);
    form.setFieldsValue({
      ...book,
      missingCopies: 0,
      damagedUnusableCopies: 0,
      damagedRepairableCopies: 0,
    });
  };

  const handleSaveEdit = async () => {
    setIsUpdating(true);
    setIsEditModalVisible(false);
    if (!editingBook) return;

    try {
      const oldBook = books.find((book) => book.key === editingBook.key);
      const changes = [];

      const updatePayload = {};
      Object.keys(editingBook).forEach((field) => {
        if (field !== "totalCopies" && oldBook[field] !== editingBook[field]) {
          updatePayload[field] = editingBook[field];
          changes.push({
            field,
            oldValue: oldBook[field],
            newValue: editingBook[field],
          });
        }
      });

      const {
        missingCopies = 0,
        damagedUnusableCopies = 0,
        damagedRepairableCopies = 0,
      } = form.getFieldsValue([
        "missingCopies",
        "damagedUnusableCopies",
        "damagedRepairableCopies",
      ]);

      const missing = parseInt(missingCopies) || 0;
      const damagedUnusable = parseInt(damagedUnusableCopies) || 0;
      const damagedRepairable = parseInt(damagedRepairableCopies) || 0;

      let available = parseInt(editingBook.copiesAvailable);
      let total = parseInt(editingBook.totalCopies);

      const totalToAffect = missing + damagedUnusable + damagedRepairable;
      if (totalToAffect > available) {
        message.error(
          "You can't mark more books than are currently available."
        );
        setIsUpdating(false);
        return;
      }

      if (missing > 0) {
        available -= missing;
        total -= missing;
        updatePayload.missingCount =
          (parseInt(oldBook.missingCount) || 0) + missing;
      }

      if (damagedUnusable > 0) {
        available -= damagedUnusable;
        total -= damagedUnusable;
        updatePayload.damagedUnusableCount =
          (parseInt(oldBook.damagedUnusableCount) || 0) + damagedUnusable;
      }

      if (damagedRepairable > 0) {
        available -= damagedRepairable;
        updatePayload.damagedRepairableCount =
          (parseInt(oldBook.damagedRepairableCount) || 0) + damagedRepairable;
      }

      updatePayload.copiesAvailable = String(available);
      updatePayload.totalCopies = String(total);

      const oldStatus = changes
        .map((change) => `${change.field}: ${change.oldValue}`)
        .join(", ");

      let newStatus = changes
        .map((change) => `${change.field}: ${change.newValue}`)
        .join(", ");

      if (missing > 0) newStatus += ` | ${missing} marked as Missing`;
      if (damagedUnusable > 0)
        newStatus += ` | ${damagedUnusable} marked as Damaged (Unusable)`;
      if (damagedRepairable > 0)
        newStatus += ` | ${damagedRepairable} marked as Damaged (Repairable)`;

      const actionType = "edit";

      await updateBook(editingBook.key, updatePayload);

      setEditingBook((prev) => ({
        ...prev,
        copiesAvailable: String(available),
        totalCopies: String(total),
        missingCount:
          missing > 0
            ? (parseInt(prev?.missingCount) || 0) + missing
            : prev?.missingCount,
        damagedUnusableCount:
          damagedUnusable > 0
            ? (parseInt(prev?.damagedUnusableCount) || 0) + damagedUnusable
            : prev?.damagedUnusableCount,
        damagedRepairableCount:
          damagedRepairable > 0
            ? (parseInt(prev?.damagedRepairableCount) || 0) + damagedRepairable
            : prev?.damagedRepairableCount,
      }));

      await logActivity(
        editingBook.title,
        oldStatus || "Condition Update",
        newStatus,
        actionType,
        0
      );

      message.success("Book updated successfully!");
      setEditingBook(null);
      setIsUpdating(false);
    } catch (error) {
      message.error("Failed to update the book. Please try again.");
      setIsUpdating(false);
    }
  };

  const handleDeleteBook = async (bookId) => {
    const bookToDelete = books.find((book) => book.key === bookId);
    const oldStatus = `  ${bookToDelete.author}`;
    const actionType = "delete";

    try {
      await deleteBook(bookId);
      setBooks(books.filter((book) => book.key !== bookId));
      logActivity(bookToDelete.title, oldStatus, "Deleted", actionType, 0);
      message.success("Book Deleted Successfully");
    } catch (error) {
      message.error("Delete Failed, Please Try Again");
    }
  };

  return (
    <>
      <div className="searchBarMain">
        <SearchBar
          onSearch={handleSearch}
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          showSearch={true}
        />
      </div>
      <div className="table-container">
        {isUpdating && (
          <div className="loading-overlay">
            <Loading />
            <span>Please Wait, Updating...</span>
          </div>
        )}
        <Table
          className="custom-table"
          columns={columns}
          dataSource={filteredBookList}
          pagination={{ pageSize: 5, size: "middle" }}
          locale={{
            emptyText: isFetching ? (
              <Spin tip="Loading books, please wait..." />
            ) : (
              "No books found. Try searching for a different title!"
            ),
          }}
          scroll={{ x: 1000 }}
        />
      </div>
      <Modal
        className="custom-modal"
        title="Book Details"
        visible={isModalVisible}
        onCancel={handleCloseModal}
        footer={false}
      >
        {selectedBook && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Book Title">
              {selectedBook.title}
            </Descriptions.Item>
            <Descriptions.Item label="Book Author">
              {selectedBook.author}
            </Descriptions.Item>
            <Descriptions.Item label="Book Category">
              {selectedBook.category}
            </Descriptions.Item>
            <Descriptions.Item label="Book ISBN">
              {selectedBook.isbn}
            </Descriptions.Item>
            <Descriptions.Item label="Book Publisher">
              {selectedBook.publisher}
            </Descriptions.Item>
            <Descriptions.Item label="Year">
              {selectedBook.year}
            </Descriptions.Item>
            <Descriptions.Item label="Book Copies Available">
              {selectedBook.copiesAvailable}
            </Descriptions.Item>
            <Descriptions.Item label="Book Total Copies">
              {selectedBook.totalCopies}
            </Descriptions.Item>
            <Descriptions.Item label="Book Shelf Location">
              {selectedBook.shelfLocation}
            </Descriptions.Item>
            <Descriptions.Item label="Book Shelf Layer Number">
              {selectedBook.layerNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Book Department">
              {selectedBook.department}
            </Descriptions.Item>
            <Descriptions.Item label="Damaged (Repairable)">
              {selectedBook.damagedRepairableCount || 0}
              {(selectedBook.damagedRepairableCount || 0) > 0 && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleOpenRepairModal(selectedBook)}
                >
                  Repair
                </Button>
              )}
            </Descriptions.Item>

            {(selectedBook.missingCount || 0) > 0 && (
              <Descriptions.Item label="Missing Copies">
                {selectedBook.missingCount || 0}
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleOpenRecoverModal(selectedBook)}
                >
                  Mark as Found
                </Button>
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Damaged (Unusable)">
              {selectedBook.damagedUnusableCount || 0}
            </Descriptions.Item>

            <Descriptions.Item label="Book Description">
              {selectedBook.bookDescription}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        className="custom-modal"
        title="Edit Book"
        visible={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingBook(null);
          form.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => setIsEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveEdit}>
            Save Changes
          </Button>,
        ]}
      >
        {editingBook && (
          <Form
            className="custom-form"
            form={form}
            layout="vertical"
            initialValues={editingBook}
            onValuesChange={(changedValues, allValues) => {
              setEditingBook({ ...editingBook, ...changedValues });
            }}
          >
            <Form.Item label="Book Title" name="title">
              <Input />
            </Form.Item>
            <Form.Item label="Book Author" name="author">
              <Input />
            </Form.Item>
            <Form.Item label="Book Category" name="category">
              <Input />
            </Form.Item>
            <Form.Item label="Book ISBN" name="isbn">
              <Input />
            </Form.Item>
            <Form.Item label="Book Publisher" name="publisher">
              <Input />
            </Form.Item>
            <Form.Item label="Book Total Copies" name="totalCopies">
              <Input />
            </Form.Item>
            <Form.Item label="Book Copies Available" name="copiesAvailable">
              <Input />
            </Form.Item>
            <Form.Item label="Book Year" name="year">
              <Input />
            </Form.Item>
            <Form.Item label="Book Shelf Location" name="shelfLocation">
              <Input />
            </Form.Item>
            <Form.Item label="Book Shelf Layer Number" name="layerNumber">
              <Input />
            </Form.Item>

            <Form.Item label="Book Department" name="department">
              <Input />
            </Form.Item>
            <Form.Item label="Book Description" name="bookDescription">
              <TextArea />
            </Form.Item>
            <Form.Item label="Mark Missing Copies" name="missingCopies">
              <Input type="number" min={0} disabled={conditionDisabled} />
            </Form.Item>

            <Form.Item
              label="Mark Damaged (Unusable) Copies"
              name="damagedUnusableCopies"
            >
              <Input type="number" min={0} disabled={conditionDisabled} />
            </Form.Item>

            <Form.Item
              label="Mark Damaged (Repairable) Copies"
              name="damagedRepairableCopies"
            >
              <Input type="number" min={0} disabled={conditionDisabled} />
            </Form.Item>
          </Form>
        )}
      </Modal>
      <Modal
        visible={isRepairModalVisible}
        title="Repair Book Copies"
        onCancel={() => setIsRepairModalVisible(false)}
        footer={null}
      >
        <Form
          onFinish={async ({ repairCount }) => {
            try {
              const docId = repairingBook.key;
              const currentRepairable =
                parseInt(repairingBook.damagedRepairableCount) || 0;
              const currentAvailable =
                parseInt(repairingBook.copiesAvailable) || 0;

              const toRepair = parseInt(repairCount);

              if (toRepair > currentRepairable) {
                message.error(
                  "You can't repair more than are marked as repairable."
                );
                return;
              }

              const newRepairable = currentRepairable - toRepair;
              const newAvailable = currentAvailable + toRepair;

              await updateBook(docId, {
                damagedRepairableCount: newRepairable,
                copiesAvailable: String(newAvailable),
              });

              await logActivity(
                repairingBook.title,
                `Damaged (Repairable): ${currentRepairable}`,
                `Damaged (Repairable): ${newRepairable} | Copies Available: ${newAvailable}`,
                "repair",
                0
              );

              message.success("Books successfully repaired!");
              setIsRepairModalVisible(false);
              setRepairingBook(null);
            } catch (err) {
              message.error("Repair failed.");
            }
          }}
          layout="vertical"
        >
          <Form.Item
            name="repairCount"
            label="Number of Copies to Repair"
            rules={[
              {
                required: true,
                message: "Please enter number of copies to repair.",
              },
            ]}
          >
            <Input
              type="number"
              min={1}
              max={repairingBook?.damagedRepairableCount}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Confirm Repair
          </Button>
        </Form>
      </Modal>
      <Modal
        visible={isRecoverModalVisible}
        title="Recover Missing Book"
        onCancel={() => setIsRecoverModalVisible(false)}
        footer={null}
      >
        <Form
          layout="vertical"
          onFinish={async ({ recoveredCount }) => {
            try {
              const currentMissing = parseInt(recoveringBook.missingCount) || 0;
              const currentTotal = parseInt(recoveringBook.totalCopies) || 0;
              const currentAvailable =
                parseInt(recoveringBook.copiesAvailable) || 0;

              const toRecover = parseInt(recoveredCount);

              if (toRecover > currentMissing) {
                message.error(
                  "You can't recover more books than are marked as missing."
                );
                return;
              }

              const newMissing = currentMissing - toRecover;
              const newTotal = currentTotal + toRecover;
              const newAvailable = currentAvailable + toRecover;

              await updateBook(recoveringBook.key, {
                missingCount: newMissing,
                totalCopies: String(newTotal),
                copiesAvailable: String(newAvailable),
              });

              await logActivity(
                recoveringBook.title,
                `Missing: ${currentMissing}`,
                `Recovered ${toRecover} book(s) | Now Missing: ${newMissing}`,
                "recover",
                0
              );

              message.success("Books successfully recovered!");
              setIsRecoverModalVisible(false);
              setRecoveringBook(null);
              setIsModalVisible(false);
            } catch (err) {
              message.error("Recovery failed.");
            }
          }}
        >
          <Form.Item
            name="recoveredCount"
            label="Number of Copies Found"
            rules={[
              {
                required: true,
                message: "Please input number of recovered copies.",
              },
            ]}
          >
            <Input type="number" min={1} max={recoveringBook?.missingCount} />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Confirm Recovery
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default AntTable;
